import classNames from 'classnames';
import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Chooser from '../../../../components/Chooser';
import { SimpleDismissableModal } from '../../../../components/modals';
import { variantWithOptions } from '../../../../lib/product';
import { f1BandSizeOptions, f3BandSizeOptions, MeasurementUnit, getDefaultMeasurementUnit } from '../../../../lib/size';
import { expectUnreachable } from '../../../../lib/util';
import * as types from '../../../../types';
import SizeOptionDetails from '../SizeOptionDetails';
import styles from './SizeOptions.module.scss';
import { useSelector } from 'react-redux';
import * as events from '../../../../lib/analytics/events';
import gsap from 'gsap';
import ScrollToPlugin from 'gsap/ScrollToPlugin';
import CustomEase from 'gsap/CustomEase';
import {
  getNanoVariantPetAgeMonths,
  getNanoVariantPetName,
  showNanoVariant,
} from '../../../../components/NanoBanner/NanoBanner';
import { getFiGrowthBook } from '../../../../lib/growthbook';
import Loading from '../../../../components/Loading';
import { useLocale } from '../../../../contexts/LocaleContext';

gsap.registerPlugin(ScrollToPlugin);
gsap.registerPlugin(CustomEase);
CustomEase.create('scrollTo', '0.65, 0, 0.35, 1');

const DynamicSizingGuide = React.lazy(() => import('../SizingGuide/DynamicSizingGuide'));

interface UnitPickerProps {
  measurementUnit: MeasurementUnit;
  onChange: (unit: MeasurementUnit) => void;
}

function UnitPicker({ measurementUnit, onChange }: UnitPickerProps) {
  const { t } = useTranslation();

  return (
    <div className={styles.unitPickerContainer}>
      <div
        className={classNames(styles.unitPickerSlider, {
          [styles.sliderCm]: measurementUnit === 'cm',
        })}
      />
      <button
        className={classNames(styles.unitPickerOption, {
          [styles.active]: measurementUnit === 'in',
        })}
        onClick={() => onChange('in')}
        type="button"
      >
        {t('unit_inches')}
      </button>
      <button
        className={classNames(styles.unitPickerOption, {
          [styles.active]: measurementUnit === 'cm',
        })}
        onClick={() => onChange('cm')}
        type="button"
      >
        {t('unit_centimeters')}
      </button>
    </div>
  );
}

interface ISizeOptionsProps {
  bandSeries: types.BandSeries;
  needsSelection?: boolean;
  onOptionChange: (changed: Partial<types.IVariantOptions>) => void;
  selectedVariant?: types.IVariant;
  title?: string;
  variants: types.IVariant[];
  startAsUnselected: boolean;
}

const SizeOptions = ({
  bandSeries,
  needsSelection,
  onOptionChange,
  selectedVariant,
  title,
  variants,
  startAsUnselected,
}: ISizeOptionsProps) => {
  const { t } = useTranslation();
  const { country } = useLocale();
  const defaultTitle = title || t('choose_the_size');
  const makersBandSelected = selectedVariant?.bandMaker !== types.BandMaker.Fi;
  const blockedSKUs = useSelector((state: types.AppState) => state.config.siteConfig.blockedOutOfStockSkus || []);
  const [measurementUnit, setMeasurementUnit] = useState<MeasurementUnit>(() => getDefaultMeasurementUnit(country));
  const showUnitPicker = bandSeries === types.BandSeries.F3 && !makersBandSelected;
  const onUnitChange = useCallback(
    (unit: MeasurementUnit) => {
      setMeasurementUnit(unit);
      events.series3CollarMeasurementUnitSwitch(country, unit);
    },
    [country],
  );
  const isSizeAvailable = useCallback(
    (size: types.SizeOption) => {
      const thisOption = {
        ...selectedVariant?.options,
        size,
      };

      const variantForSize = variantWithOptions(variants, thisOption);
      return variantForSize && !(blockedSKUs ?? []).includes(variantForSize.sku);
    },
    [selectedVariant?.options, variants, blockedSKUs],
  );

  const allSizesWithDetails = useMemo(() => {
    const sizeSet = new Set<types.SizeOption>();
    variants.forEach((v) => sizeSet.add(v.options.size));

    if (bandSeries === types.BandSeries.F1) {
      return f1BandSizeOptions
        .filter((s) => sizeSet.has(s))
        .map((s) => ({
          size: s,
          isAvailable: isSizeAvailable(s),
        }));
    } else if (bandSeries === types.BandSeries.F3) {
      return f3BandSizeOptions
        .filter((s) => sizeSet.has(s))
        .map((s) => ({
          size: s,
          isAvailable: isSizeAvailable(s),
        }));
    } else {
      expectUnreachable(bandSeries);
      throw new Error(`Unsupported band series: ${bandSeries}`);
    }
  }, [bandSeries, isSizeAvailable, variants]);

  const sizeOptionRefs = allSizesWithDetails.reduce((acc, { size }) => {
    acc[size] = React.createRef();
    return acc;
  }, {} as Record<string, React.RefObject<HTMLDivElement>>);
  const scrollToSizeOption = (size: types.SizeOption) => {
    const element = sizeOptionRefs[size]?.current;
    if (!element) return;

    const viewportHeight = window.innerHeight;
    const rect = element.getBoundingClientRect();
    const offsetY = viewportHeight / 2 - rect.height / 2;

    gsap.to(window, {
      duration: 1.2,
      scrollTo: { y: element, offsetY: offsetY },
      ease: 'scrollTo',
    });
  };

  const [showSelected, setShowSelected] = useState<boolean>(!startAsUnselected);
  const sizeChooser = useMemo(() => {
    return (
      <Chooser
        onSelect={(size: types.SizeOption) => {
          setShowSelected(true);
          onOptionChange({ size });
        }}
        selectedOption={showSelected ? selectedVariant?.options.size : undefined}
        groups={[
          {
            options: allSizesWithDetails.map(({ size, isAvailable }) => ({
              content: (
                <SizeOptionDetails
                  sizeOptionRef={sizeOptionRefs[size]}
                  sizeOption={size}
                  bandSeries={bandSeries}
                  bandMaker={selectedVariant?.bandMaker}
                  color={selectedVariant?.options.color}
                  measurementUnit={measurementUnit}
                />
              ),
              disabled: !isAvailable,
              value: size,
            })),
          },
        ]}
      />
    );
  }, [allSizesWithDetails, onOptionChange, bandSeries, selectedVariant, showSelected, sizeOptionRefs, measurementUnit]);
  const sizingGuide = (
    <Suspense fallback={<Loading />}>
      {' '}
      {!makersBandSelected && (
        <div className={styles.moreInfo}>
          <SimpleDismissableModal
            onOpen={() => events.series3CollarWhatSizeIsRight()}
            trigger={
              <button className={styles.sizingGuideButton}>
                {t('what_size_is_right')}
                <div className={styles.buttonArrowAngle} />
              </button>
            }
          >
            <DynamicSizingGuide
              bandSeries={bandSeries}
              onSelect={(size: types.SizeOption) => {
                setShowSelected(true);
                onOptionChange({ size });
                scrollToSizeOption(size);
              }}
              measurementUnit={measurementUnit}
            />
          </SimpleDismissableModal>
        </div>
      )}
    </Suspense>
  );
  const nanoVariantEnabled = showNanoVariant();
  const petName = getNanoVariantPetName();
  const petAge = getNanoVariantPetAgeMonths();
  const nanoSizeSuggestionEnabled =
    nanoVariantEnabled && getFiGrowthBook().getFeatureValue('nano-registration-size-suggestion-experiment', false);
  const subtitleText =
    petName && petAge <= 6
      ? t('when_pet_name_is_fully_grown_well_swap_it_out_for_a_larger_size_for_free', { petName })
      : nanoSizeSuggestionEnabled
      ? t('based_on_pet_name_breed_and_weight', { petName })
      : null;

  return (
    <>
      <div className={classNames(styles.sizeVariant, 'size-options')}>
        <div className={styles.variantTitleGroup}>
          <div className={styles.variantTitleRow}>
            <div className={classNames(styles.variantTitle, needsSelection ? styles.variantTitleError : undefined)}>
              {defaultTitle}
            </div>
            {showUnitPicker && <UnitPicker measurementUnit={measurementUnit} onChange={onUnitChange} />}
          </div>
          {nanoVariantEnabled && subtitleText && <div className={styles.nanoSizeDescription}>{subtitleText}</div>}
          {/* Disable sizing guide for F1/F2 collar since users may be directed to out of stock sizes */}
          {!nanoSizeSuggestionEnabled && bandSeries !== types.BandSeries.F1 && sizingGuide}
        </div>

        {sizeChooser}
        {nanoSizeSuggestionEnabled && sizingGuide}
      </div>
    </>
  );
};

export default SizeOptions;
